import { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Accordion } from '@core/components';
import type { Vestment } from '../../../../api/vestment';
import { ChasubleCollarFields } from '../../../components/ChasubleValueFields';
import { useAppContext } from '../../../context/AppContext';
import {
  getChasubleFeatures,
  showsChasubleCollar,
} from '../../../utils/chasuble';
import {
  findCollarByName,
  getCollarLabel,
  getCollarOptions,
  isChineseCollar,
} from '../../../utils/collar';
import type {
  ChasubleItemFormValues,
  OrderItemDialogSlotProps,
} from '../../../utils/orderItem';

/**
 * Picking another vestment moves the collar to the new vestment's collar with
 * the same name, since each vestment has its own collar ids, and clears it
 * when there is none.
 *
 * For `collarDefaults` types: picking the vestment fills an empty collar with
 * its Chinese Collar and the body fabric and color, and later body changes
 * carry over to collar values that still match the previous body value. The
 * color only follows while the collar fabric is the body fabric.
 */
function useCollarDefaults(
  methods: UseFormReturn<ChasubleItemFormValues>,
  vestment?: Vestment
) {
  const { collars } = useAppContext();
  const vestmentType = vestment?.type;
  const enabled = getChasubleFeatures(vestmentType)?.collarDefaults ?? false;
  const bodyFabric = methods.watch('bodyFabric');
  const bodyColor = methods.watch('bodyColor');
  const prevVestment = useRef(vestment);
  const prevBody = useRef({ fabric: bodyFabric, color: bodyColor });

  useEffect(() => {
    const prev = prevVestment.current;
    prevVestment.current = vestment;
    if (!vestment || prev?.id === vestment.id) {
      return;
    }
    const options = getCollarOptions(vestment, collars);
    const collar = methods.getValues('collar');
    if (collar) {
      const match = prev
        ? findCollarByName(options, getCollarLabel(prev, collars, collar))
        : options.find((option) => String(option.id) === collar);
      const nextCollar = match ? String(match.id) : '';
      if (nextCollar !== collar) {
        methods.setValue('collar', nextCollar, { shouldDirty: true });
      }
    }
    if (!enabled) {
      return;
    }
    const values = methods.getValues();
    const chineseCollar = options.find(isChineseCollar);
    if (!values.collar && chineseCollar) {
      methods.setValue('collar', String(chineseCollar.id), {
        shouldDirty: true,
      });
    }
    if (!values.collarFabric) {
      methods.setValue('collarFabric', values.bodyFabric, {
        shouldDirty: true,
      });
    }
    if (
      !values.collarColor &&
      methods.getValues('collarFabric') === values.bodyFabric
    ) {
      methods.setValue('collarColor', values.bodyColor, { shouldDirty: true });
    }
  }, [collars, enabled, methods, vestment]);

  useEffect(() => {
    const prev = prevBody.current;
    prevBody.current = { fabric: bodyFabric, color: bodyColor };
    if (!enabled) {
      return;
    }
    const follows = (value: string, previous: string) =>
      !value || value === previous;
    const { collarFabric, collarColor } = methods.getValues();
    if (bodyFabric !== prev.fabric && follows(collarFabric, prev.fabric)) {
      methods.setValue('collarFabric', bodyFabric, { shouldDirty: true });
    }
    if (
      bodyColor !== prev.color &&
      methods.getValues('collarFabric') === bodyFabric &&
      follows(collarColor, prev.color)
    ) {
      methods.setValue('collarColor', bodyColor, { shouldDirty: true });
    }
  }, [bodyColor, bodyFabric, enabled, methods]);
}

interface CollarSectionProps {
  methods: UseFormReturn<ChasubleItemFormValues>;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  vestment?: Vestment;
  setPreviewTab: OrderItemDialogSlotProps<ChasubleItemFormValues>['setPreviewTab'];
}

export function CollarSection({
  methods,
  expanded,
  onChange,
  vestment,
  setPreviewTab,
}: CollarSectionProps) {
  useCollarDefaults(methods, vestment);

  return (
    <Accordion
      title="Collar"
      expanded={expanded}
      collapsed={!showsChasubleCollar(vestment?.type)}
      onChange={onChange}
    >
      <ChasubleCollarFields<ChasubleItemFormValues>
        vestment={vestment}
        onTypeChange={() => setPreviewTab('vestment')}
      />
    </Accordion>
  );
}
